<script setup lang="ts">
import {
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    ZhEmptyState,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
} from '@zerohuman/ui'
import { onMounted, ref } from 'vue'
import { api, type ConnectionRow, formatAgo } from '@/api'
import DocsLink from '@/components/DocsLink.vue'
import { docsUrl } from '@/lib/docs'

/*
 * #333 — the "Connected apps" screen, beside API tokens because it answers the same question about a
 * different kind of credential: what can reach this enterprise, acting as whom, and how do I stop it.
 *
 * Scopes are read only here. Changing what a connected app may do is a fresh authorization by its client,
 * not an edit a member can make behind its back, so there is deliberately no equivalent of the token
 * screen's "Edit scopes".
 */
const connections = ref<ConnectionRow[]>([])
const error = ref<string | null>(null)
const loading = ref(true)
const busy = ref(false)
const now = ref(Date.now())

/** An app connects itself, so the empty state's one action is the guide to connecting an agent over MCP. */
const connectDocs = docsUrl('/docs/mcp/connected-apps')

/** What to call a connection: the name its client gave at consent, or the client id when it gave none. */
function label(row: ConnectionRow): string {
    return row.clientName ?? row.clientId
}

async function load(): Promise<void> {
    try {
        connections.value = await api.connections()
        error.value = null
    } catch (caught) {
        error.value =
            caught instanceof Error
                ? `Could not load connected apps (${caught.message}).`
                : 'Could not load connected apps.'
    } finally {
        loading.value = false
    }
}

onMounted(load)

/**
 * Revoke takes effect on the connection's next request, so the list is reloaded afterwards rather than
 * patched in place: what the API now holds is the only honest answer about whether it is still live.
 */
async function revoke(row: ConnectionRow): Promise<void> {
    if (!confirm(`Revoke "${label(row)}"? Its next request is refused.`)) {
        return
    }
    busy.value = true
    error.value = null
    try {
        await api.revokeConnection(row.id)
        await load()
    } catch (caught) {
        error.value = caught instanceof Error ? `That did not work (${caught.message}).` : 'That did not work.'
    } finally {
        busy.value = false
    }
}
</script>

<template>
	<section class="w-full space-y-6 p-8">
		<!--
			The kit title and heading read their text from the slot: a `title` prop would be silently dropped.
			The `Plug` icon is dropped on purpose. The kit title draws no icon (it takes a kicker, the text and a
			meta line), and no migrated screen passes one. Before, `:icon` and `title` only landed as attributes
			on the header, so the screen showed an empty title and no icon.
		-->
		<ZhPageTitle>
			Connected apps
			<template #meta><DocsLink path="/docs/mcp/connected-apps" /></template>
		</ZhPageTitle>
		<p class="text-muted-foreground max-w-2xl text-sm">
			An external agent connects to this enterprise through the OS MCP server, acting as one of your team
			members with the scopes a person approved at consent. Revoking one refuses its next request; the row is
			kept, marked revoked, so the audit trail outlives the grant. Changing what an app may do is a fresh
			authorization by the app itself, so scopes are read-only here.
		</p>

		<p v-if="error" class="text-destructive text-sm" data-testid="error">{{ error }}</p>

		<ZhPanel class="space-y-3" aria-labelledby="existing-connections">
			<ZhSectionHeading id="existing-connections">Connections</ZhSectionHeading>
			<p v-if="loading" class="text-muted-foreground text-sm">Loading…</p>
			<!-- "Nothing is connected" and "we could not ask" are different answers: a failed load says so above. -->
			<ZhEmptyState v-else-if="error === null && connections.length === 0" data-testid="no-connections">
				No app is connected yet. External agents you approve appear here, with the member each acts as and
				the scopes it holds.
				<template #action>
					<Button as-child size="sm">
						<a :href="connectDocs" target="_blank" rel="noopener noreferrer">Connect an agent over MCP</a>
					</Button>
				</template>
			</ZhEmptyState>
			<Table v-else-if="connections.length > 0">
				<TableHeader>
					<TableRow>
						<TableHead>App</TableHead>
						<TableHead>Approved by</TableHead>
						<TableHead>Acts as</TableHead>
						<TableHead>Scopes</TableHead>
						<TableHead>Last used</TableHead>
						<TableHead>Created</TableHead>
						<TableHead></TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					<TableRow v-for="row in connections" :key="row.id" :data-testid="`connection-${row.id}`">
						<TableCell :class="row.revokedAt ? 'text-muted-foreground line-through' : ''">
							{{ label(row) }}
						</TableCell>
						<TableCell>{{ row.approvedBy?.name ?? 'unknown' }}</TableCell>
						<TableCell>{{ row.member?.name ?? 'unknown' }}</TableCell>
						<TableCell class="font-mono text-xs whitespace-normal">{{ row.scopes.join(' ') }}</TableCell>
						<TableCell>{{ row.lastUsedAt ? formatAgo(now - Date.parse(row.lastUsedAt)) : 'never' }}</TableCell>
						<TableCell>{{ formatAgo(now - Date.parse(row.createdAt)) }}</TableCell>
						<TableCell class="text-right">
							<span v-if="row.revokedAt" class="text-muted-foreground text-xs" :data-testid="`revoked-${row.id}`">
								revoked
							</span>
							<Button
								v-else
								size="sm"
								variant="outline"
								:data-testid="`revoke-${row.id}`"
								:disabled="busy"
								@click="revoke(row)"
							>
								Revoke
							</Button>
						</TableCell>
					</TableRow>
				</TableBody>
			</Table>
		</ZhPanel>
	</section>
</template>
